// ── DESIGNER HOURS SECTION ────────────────────────────────────────────────────
// Toggl pull -> Claude category narratives -> PDF generation pipeline.
// Lifted verbatim from BillingTab.jsx (was activeSection === 'hourly').
//
// Self-contained: owns its own Toggl/Claude/PDF state, has its own PDF preview
// modal, persists to the existing `hours_invoices` table (separate from the
// `invoices` table that holds deposit/final/reimbursable invoices).
//
// Mounted as a sub-tab inside InvoicesTab so all client billing is in one place.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import PaymentsPanel from './PaymentsPanel';
import HoursInvoiceEditor from './HoursInvoiceEditor';
import { calcPaidForDocument, derivePaidStatus, paymentEntriesForDocument, hasAdminAccess, computeEffectiveCategoryData, groupHoursByPersonAsCategories, clientHoursGrouping, activeProjects } from '../utils';
import { ALERT_AMBER_TEXT, HAIRLINE_AMBER } from '../lib/designTokens';

function fmtFull(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const inp = {
  padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff',
  boxSizing: 'border-box',
};

// ── Pure helpers (module-scope, exported for testing) ─────────────────────────
// Both saveDraft and saveToRecords build their hours_invoices row through
// buildHoursInvoiceRow. Keeping the column shape in one place was the
// missing guardrail for the 2026-04-17 production bug where saveToRecords
// wrote a partial payload (status + pdf_url + totals) and an INSERT path
// produced rows with project_label / invoice_date / categories_snapshot /
// summaries all NULL on 26-Moskito-2026-03 and 26-Ptarmigan-2026-03.
//
// New columns added to hours_invoices in the future MUST be added here once
// — both call sites pick them up automatically. Guard test:
// __tests__/designerHoursFinalize.test.jsx.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Raw Toggl entries as paste-ready text (2026-08-04 — Cimon summarizes
// hours in her own Claude chat; Gilda's Regenerate/Consolidate are the
// optional in-app alternative). Pure, exported for tests.
export function buildRawEntriesText(cat) {
  if (!cat) return '';
  // Person-pivot rows carry effHours/totalHours instead of the raw
  // pull's cat.hours — accept any of them for the header line.
  var catHrs = Number(cat.hours != null ? cat.hours : (cat.effHours != null ? cat.effHours : cat.totalHours)) || 0;
  var lines = [cat.name.toUpperCase() + ' — ' + catHrs.toFixed(1) + ' hrs'];
  (cat.entries || []).forEach(function(e) {
    lines.push('');
    lines.push(e.togglName + (e.role ? ' (' + e.role + ')' : '') + ' — ' + (Number(e.hours) || 0).toFixed(1) + ' hrs');
    (e.timeEntries || []).forEach(function(t) {
      var h = t.duration ? (t.duration / 3600) : 0;
      lines.push('- ' + (t.date || '') + ' · ' + h.toFixed(2) + 'h · ' + (t.description || '(no note)'));
    });
  });
  return lines.join('\n');
}

export function buildAllRawEntriesText(apiResult, header) {
  if (!apiResult) return '';
  var out = [];
  if (header) out.push(header, '');
  (apiResult.categories || []).forEach(function(cat) {
    if (!cat || !(cat.entries || []).length) return;
    out.push(buildRawEntriesText(cat), '');
  });
  return out.join('\n').trim();
}

// Toggl usernames the pull SKIPPED because no row on the project's team
// table (Projects → Integrations → Toggl Name) matches them exactly
// (2026-10-30). The endpoint returns them as `unmatchedUsers`; before
// that the only symptom of a missing or misspelled Toggl Name was a
// person's hours silently absent from the invoice. Returns [] for an
// older response shape or when everyone matched.
export function unmatchedTogglUsers(apiResult) {
  if (!apiResult || !Array.isArray(apiResult.unmatchedUsers)) return [];
  return apiResult.unmatchedUsers
    .filter(function(u) { return u && u.togglName && Number(u.hours) > 0; })
    .map(function(u) {
      return {
        togglName: String(u.togglName),
        hours: Number(u.hours),
        phases: Array.isArray(u.phases) ? u.phases.filter(Boolean) : [],
      };
    });
}

// One line per skipped user: "Veronica Vaskinn-Lew · 2.00 hrs · Concept
// Development, Design Development". Plain text so the same string can
// go to the warning block and the console.
export function unmatchedTogglUserLine(u) {
  var hrs = Number(u.hours || 0).toFixed(2) + (Number(u.hours) === 1 ? ' hr' : ' hrs');
  var ph = (u.phases || []).length ? ' · ' + u.phases.join(', ') : '';
  return u.togglName + ' · ' + hrs + ph;
}

export function buildInvoiceNumber(projLabel, year, month) {
  return `${projLabel}-${year}-${String(month).padStart(2, '0')}`;
}

// Single source of truth for the hours_invoices row written by EITHER
// saveDraft (status='Draft') or saveToRecords (status='Final', pdfUrl set).
// args: { selProj, client, apiYear, apiMonth, apiResult,
//         effTotalHours, effTotalAmount, adjustedHours, adjustmentNotes,
//         summaries, status, pdfUrl }
export function buildHoursInvoiceRow(args) {
  const monthName = MONTH_NAMES[args.apiMonth - 1] || '';
  return {
    invoice_number:      buildInvoiceNumber(args.selProj.label, args.apiYear, args.apiMonth),
    project_label:       args.selProj.label,
    client_name:         (args.client && args.client.name) || '',
    invoice_date:        `${monthName} ${args.apiYear}`,
    status:              args.status,
    total_hours:         args.effTotalHours,
    total_amount:        args.effTotalAmount,
    categories_snapshot: (args.apiResult && args.apiResult.categories) || [],
    adjusted_hours:      args.adjustedHours   || {},
    adjustment_notes:    args.adjustmentNotes || {},
    summaries:           args.summaries       || {},
    pdf_url:             args.pdfUrl          || '',
    updated_at:          new Date().toISOString(),
  };
}

// Pre-flight gate for finalizing a Designer Hours invoice. Refuses to let
// the click reach supabase when state is incomplete, so an INSERT can
// never produce a row with most columns NULL. The "Pull from Toggl" copy
// is what the bookkeeper sees if they hit Save to Records before pulling
// fresh Toggl data — the same code path that produced the 2026-04-17 rows.
// state: { apiResult, effTotalHours, effTotalAmount, selProj }
export function canFinalizeHoursInvoice(state) {
  if (!state || !state.selProj) {
    return { ok: false, error: 'Pick a project before finalizing.' };
  }
  if (!state.apiResult) {
    return { ok: false, error: 'Pull from Toggl before finalizing.' };
  }
  const hours  = Number(state.effTotalHours);
  const amount = Number(state.effTotalAmount);
  if (!(hours > 0) || !(amount > 0)) {
    return { ok: false, error: 'Pull from Toggl before finalizing.' };
  }
  return { ok: true };
}

export default function DesignerHoursSection() {
  const ctx = useAppContext();
  const projects     = ctx.projects     || [];
  const clients      = ctx.clients      || [];
  const firmSettings = ctx.firmSettings || {};
  const userEmail    = ctx.userEmail    || '';
  const isOwner      = !!ctx.isOwner;
  const canManage    = isOwner || hasAdminAccess(userEmail, firmSettings);
  const clientPayments       = ctx.clientPayments       || [];
  const paymentAllocations   = ctx.paymentAllocations   || [];
  const recordClientPayment  = ctx.recordClientPayment;
  const removePaymentAllocation = ctx.removePaymentAllocation;

  // ── STATE ──
  const fileRef = useRef(null);
  const [togglData,             setTogglData]             = useState(null);
  const [togglError,            setTogglError]            = useState('');
  const [apiProjId,             setApiProjId]             = useState('');
  const [apiMonth,              setApiMonth]              = useState(new Date().getMonth() + 1);
  const [apiYear,               setApiYear]               = useState(new Date().getFullYear());
  const [apiPulling,            setApiPulling]            = useState(false);
  const [apiPullError,          setApiPullError]          = useState('');
  const [apiResult,             setApiResult]             = useState(null);
  const [summaries,             setSummaries]             = useState({});
  // Tracks whether the current in-memory session was loaded from a
  // Draft or Final hours_invoices row (set by loadDraft) vs. a fresh
  // Toggl pull (null). The autosave useEffect skips when loadedStatus
  // is 'Final' so it doesn't auto-convert a Final record back to Draft.
  const [loadedStatus,          setLoadedStatus]          = useState(null);
  const [summarizingCat,        setSummarizingCat]        = useState(null);
  const [adjustedHours,         setAdjustedHours]         = useState({});
  const [adjustmentNotes,       setAdjustmentNotes]       = useState({});
  const [consolidatingCat,      setConsolidatingCat]      = useState(null);
  const [pdfModalUrl,           setPdfModalUrl]           = useState(null);
  const [pdfModalFilename,      setPdfModalFilename]      = useState('');
  const [pdfGenerating,         setPdfGenerating]         = useState(false);
  const [pdfError,              setPdfError]              = useState('');
  const [pdfBlobBytes,          setPdfBlobBytes]          = useState(null);
  const [draftSaveStatus,       setDraftSaveStatus]       = useState('');
  const [draftSaveError,        setDraftSaveError]        = useState('');
  const [draftBanner,           setDraftBanner]           = useState(null);
  const [hoursInvoices,         setHoursInvoices]         = useState([]);
  const [hoursInvoicesLoading,  setHoursInvoicesLoading]  = useState(true);
  const [savingToRecords,       setSavingToRecords]       = useState(false);
  const [saveToRecordsError,    setSaveToRecordsError]    = useState('');
  const [paymentsModalInv,      setPaymentsModalInv]      = useState(null);  // hours_invoice row open in payments modal
  const [inspectingInv,         setInspectingInv]         = useState(null);  // finalized row open in the read-only inspection modal; null when closed

  // Persist the lifecycle status on the hours_invoices row from the relational
  // allocation set. 'Paid' when sum-of-allocations meets the asked amount,
  // otherwise walks back to 'Final' (or 'Draft' if it was draft to begin with).
  async function syncHoursInvoiceStatus(invRow, allocs) {
    const amt = parseFloat(invRow.total_amount) || 0;
    const derived = derivePaidStatus(allocs, 'hours_invoice', invRow.invoice_number, amt, invRow.status);
    // hours_invoices uses Draft/Final/Partial/Paid casing — translate from
    // the lowercased lifecycle the helper returns.
    let nextStatus = invRow.status || 'Final';
    if (derived === 'paid')         nextStatus = 'Paid';
    else if (derived === 'partial') nextStatus = 'Partial';
    else if (invRow.status === 'Paid' || invRow.status === 'Partial') nextStatus = 'Final';
    if (nextStatus === invRow.status) return invRow;
    const { data, error } = await supabase.from('hours_invoices')
      .update({ status: nextStatus, updated_at: new Date().toISOString() })
      .eq('invoice_number', invRow.invoice_number)
      .select().single();
    if (error) { console.error('hours_invoices status sync failed:', error); return invRow; }
    return data || invRow;
  }

  // PaymentsPanel onRecordPayment(payment, allocations) bridge for hours
  // invoices. Persists via context, reloads the row.
  async function onPanelRecordPayment(invRow, payment, allocations) {
    if (!recordClientPayment) return;
    const res = await recordClientPayment(payment, allocations);
    if (res && res.ok) {
      const nextAllocs = paymentAllocations.concat(res.allocations || []);
      const updated = await syncHoursInvoiceStatus(invRow, nextAllocs);
      setPaymentsModalInv(updated);
      loadHoursInvoices();
    }
  }

  // PaymentsPanel onRemoveAllocation(allocationId) bridge.
  async function onPanelRemoveAllocation(invRow, allocationId) {
    if (!removePaymentAllocation) return;
    await removePaymentAllocation(allocationId);
    const nextAllocs = paymentAllocations.filter(a => a.id !== allocationId);
    const updated = await syncHoursInvoiceStatus(invRow, nextAllocs);
    setPaymentsModalInv(updated);
    loadHoursInvoices();
  }

  // ── LOAD ──
  useEffect(() => {
    loadHoursInvoices();
  }, []);

  // Escape closes the read-only inspection modal. Backdrop click +
  // explicit Close button handled inline on the modal block.
  useEffect(() => {
    if (!inspectingInv) return undefined;
    const handler = (e) => { if (e.key === 'Escape') setInspectingInv(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [inspectingInv]);

  async function loadHoursInvoices() {
    setHoursInvoicesLoading(true);
    const { data } = await supabase.from('hours_invoices')
      .select('*').order('updated_at', { ascending: false }).limit(20);
    if (data) setHoursInvoices(data);
    setHoursInvoicesLoading(false);
  }

  // ── HANDLERS ──

  function handleTogglFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setTogglError('');
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const lines = ev.target.result.split('\n').filter(Boolean);
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
        const rows = lines.slice(1).map(line => {
          const cols = []; let inQ = false, cur = '';
          for (const ch of line) {
            if (ch === '"') { inQ = !inQ; }
            else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
            else cur += ch;
          }
          cols.push(cur.trim());
          const row = {}; headers.forEach((h, i) => row[h] = cols[i] || ''); return row;
        });
        const byProject = {};
        rows.forEach(r => {
          const proj = r.project || r['project name'] || 'Unassigned';
          if (!byProject[proj]) byProject[proj] = { hours: 0, amount: 0, entries: 0 };
          const dur = r.duration || r['dur'] || '0:00:00';
          const parts = dur.split(':').map(Number);
          const hrs = (parts[0] || 0) + (parts[1] || 0) / 60 + (parts[2] || 0) / 3600;
          byProject[proj].hours += hrs;
          byProject[proj].amount += parseFloat((r.amount || r['billable amount'] || '0').replace(/[$,]/g, '')) || 0;
          byProject[proj].entries++;
        });
        setTogglData({ byProject, totalHours: Object.values(byProject).reduce((s, p) => s + p.hours, 0), totalAmount: Object.values(byProject).reduce((s, p) => s + p.amount, 0), rowCount: rows.length });
      } catch (err) { setTogglError('Could not parse CSV. Make sure it is a Toggl Track detailed report export.'); }
    };
    reader.readAsText(file);
  }

  async function pullFromToggl() {
    const selProj = projects.find(pr => String(pr.id) === String(apiProjId));
    if (!selProj) return;
    if (!firmSettings?.togglWorkspaceId) {
      setApiPullError('Toggl Workspace ID not set. Add it in Admin → Settings.');
      return;
    }
    const lastDay = new Date(apiYear, apiMonth, 0).getDate();
    const pad = n => String(n).padStart(2, '0');
    const startDate = `${apiYear}-${pad(apiMonth)}-01`;
    const endDate   = `${apiYear}-${pad(apiMonth)}-${pad(lastDay)}`;
    const nonBillable = (firmSettings?.nonBillableTogglProjects || '').split('\n').map(s => s.trim()).filter(Boolean);
    const requestBody = {
      workspaceId: firmSettings.togglWorkspaceId,
      togglPrefix: selProj.togglPrefix,
      startDate,
      endDate,
      teamMembers: selProj.teamMembers || [],
      nonBillableProjects: nonBillable,
    };

    const invoiceNumber = buildInvoiceNumber(selProj.label, apiYear, apiMonth);
    const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][apiMonth - 1];
    const { data: existingDraft } = await supabase.from('hours_invoices').select('*').eq('invoice_number', invoiceNumber).maybeSingle();
    if (existingDraft) {
      setDraftBanner({ invoiceNumber, record: existingDraft, monthName, projLabel: selProj.label, requestBody });
      return;
    }

    await _doPullFresh(requestBody);
  }

  async function _doPullFresh(requestBody) {
    setDraftBanner(null);
    setApiPulling(true);
    setApiPullError('');
    setApiResult(null);
    setSummaries({});
    setAdjustedHours({});
    setAdjustmentNotes({});
    setLoadedStatus(null);
    try {
      const res = await fetch('/api/toggl-entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'API error');
      setApiResult(data);
    } catch (err) {
      setApiPullError(err.message || 'Failed to pull from Toggl.');
    } finally {
      setApiPulling(false);
    }
  }

  async function summarizeCategory(cat) {
    try {
      const descriptions = cat.entries
        .flatMap(e => (e.timeEntries || []).map(t => t.description))
        .filter(Boolean)
        .join('\n');
      const selProj = projects.find(pr => String(pr.id) === String(apiProjId));
      const projectLabel = selProj?.label || '';
      setSummarizingCat(cat.name);
      // Person mode (2026-08-23, the Kubik switch): the row is a team
      // member, not a phase — the exemplar format wants a SHORT prose
      // description of that person's month (2–3 sentences, no bullets).
      if (personMode) {
        try {
          const res = await fetch('/api/claude', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              system: `You are a billing assistant for Cimon Starr Interior Design, a high-end residential interior design firm. Write the one-line description of a team member's work for a client time-charges invoice.

Rules:
- 2 to 3 short sentences of plain prose. No bullets, no headers, no markdown.
- Be specific: name rooms, items, vendors, and deliverables from the entries where known.
- Never mention hours, rates, dollar amounts, or other people's names.
- No filler words (seamless, comprehensive, ensured, facilitated, leveraged).`,
              user: `Project: ${projectLabel}\n\nThis person's time entries this month:\n${descriptions || '(no entries)'}\n\nWrite the 2–3 sentence description of their work.`,
              max_tokens: 300,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.error || ('API error ' + res.status));
          const text = data?.content?.[0]?.text || data?.text || '';
          if (!text.trim()) throw new Error('empty response');
          setSummaries(prev => ({ ...prev, [cat.name]: text.trim() }));
        } catch (err) {
          window.alert('Summarize failed for ' + cat.name + ': ' + (err && err.message ? err.message : String(err)) + '\n\nTry again, or paste your own description — the box stays editable.');
        } finally {
          setSummarizingCat(null);
        }
        return;
      }
      try {
        const systemPrompt = `You are a billing assistant for Cimon Starr Interior Design, a high-end residential interior design firm. Generate a billing summary for a client invoice in bullet point format.

Rules:
- Write one bullet point per distinct activity or deliverable — do not combine unrelated tasks
- Each bullet must be specific: name the room, item, vendor, or decision involved where known
- Do not use filler language: never use words like seamless, comprehensive, ensured, encompassed, facilitated, leveraged, streamlined, or similar corporate padding
- Do not start every bullet the same way
- Stay strictly within the scope of the billing category provided — do not include work that belongs in another category
- Category scope definitions:
  Concept & Design Development: material selections, finish selections, furniture specifications, presentation boards, design concepts, proposal preparation, vendor sourcing and research. Exclude: construction documentation, site visits, contractor meetings.
  Construction Management: CAD drawings, construction documentation, interior elevations, lighting plans, finish schedules, contractor coordination, site meetings, bid sets, millwork details, sheet revisions. Exclude: furniture selection, client design presentations.
  Procurement: purchase orders, vendor quotes, order tracking, proposal creation and revision, pricing verification, delivery coordination, vendor correspondence on active orders. Exclude: design work, construction documentation.
  Project Administration: site visits, client meetings, travel, project scheduling, contractor correspondence, logistics, shipping coordination, operational planning. Exclude: design work, construction documentation, procurement activities.
- Use professional interior design language
- Do not mention team member names or hourly rates
- Do not mention dollar amounts
- Format: plain bullet points starting with • — no headers, no bold, no markdown`;
        const userPrompt = `Category: ${cat.name}
Project: ${projectLabel}

Time entries this month:
${descriptions || '(no entries)'}

Write bullet points summarizing the work performed in this category only. One bullet per distinct activity. Be specific — use room names, vendor names, and item names from the entries where available.`;
        const res = await fetch('/api/claude', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ system: systemPrompt, user: userPrompt, max_tokens: 600 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || ('API error ' + res.status));
        const text = data?.content?.[0]?.text || data?.text || '';
        // An empty response must NEVER silently blank the narrative
        // (2026-08-05 — the Construction Management box 'disappeared'
        // because '' fell out of an unexpected response shape).
        if (!text.trim()) throw new Error('empty response');
        setSummaries(prev => ({ ...prev, [cat.name]: text }));
      } catch (err) {
        window.alert('Summarize failed for ' + cat.name + ': ' + (err && err.message ? err.message : String(err)) + '\n\nTry again, or paste your own narrative — the box stays editable.');
      } finally {
        setSummarizingCat(null);
      }
    } catch (err) {
      console.error('[summarizeCategory] ERROR:', err);
    }
  }

  async function consolidateSummary(catName) {
    const current = summaries[catName];
    if (!current) return;
    setConsolidatingCat(catName);
    try {
      const res = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: `You are a billing assistant for Cimon Starr Interior Design. You are consolidating a bullet point list for a client invoice.

Rules:
- Remove duplicate or near-duplicate bullets that describe the same activity
- Combine bullets that are minor variations of the same task into one specific bullet
- Keep the most descriptive and specific version when combining
- Remove minor administrative bullets that don't represent significant billable work
- Target 6-10 bullets total — fewer if the work was focused, more if genuinely varied
- Keep all bullets that represent distinct significant deliverables
- Preserve specific room names, vendor names, and item names
- Output only the consolidated bullet points starting with • — no commentary, no headers`,
          user: `Consolidate these billing bullets for ${catName}:\n\n${current}`,
          max_tokens: 600,
        }),
      });
      const data = await res.json();
      const text = data?.content?.[0]?.text || data?.text || '';
      if (text) setSummaries(prev => ({ ...prev, [catName]: text }));
    } catch (err) {
      console.error('[Consolidate error]', catName, err);
    } finally {
      setConsolidatingCat(null);
    }
  }

  async function generatePdf() {
    if (!apiResult) return;
    if (pdfModalUrl) URL.revokeObjectURL(pdfModalUrl);
    setPdfModalUrl(null);
    setPdfGenerating(true);
    setPdfError('');
    try {
      const selProj = projects.find(pr => String(pr.id) === String(apiProjId));
      const client  = selProj ? clients.find(c => c.id === selProj.clientId) : null;
      const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][apiMonth - 1];
      const invoiceCats = effCategoryData.filter(cat => cat.effHours > 0);
      const allAdjustments = [];
      invoiceCats.forEach(cat => {
        cat.effEntries.forEach(entry => {
          const key = `${cat.name}__${entry.togglName}`;
          if (adjustedHours[key] != null && Number(adjustedHours[key]) !== entry.hours) {
            allAdjustments.push({ catName: cat.name, entry, billedHrs: Number(adjustedHours[key]) });
          }
        });
      });
      const invoiceNumber = selProj ? buildInvoiceNumber(selProj.label, apiYear, apiMonth) : '';
      const payload = {
        invoiceDate:   `${monthName} ${apiYear}`,
        invoiceNumber,
        projectName:   selProj?.displayName || selProj?.label || '',
        billTo: client ? {
          name:    client.name           || '',
          address: client.billingAddress || '',
          city:    client.billingCity    || '',
          state:   client.billingState   || '',
          zip:     client.billingZip     || '',
        } : {},
        // Person mode (Kubik): rows are people; rate prints as its own
        // column on the PDF. Phase mode: rate stays absent (no column).
        // Names print via each team member's optional "Name on invoice"
        // (Project Directory → Integrations); blank falls back to the
        // Toggl name. The Toggl name itself is NEVER prettified — it's
        // the API match key (a mismatch silently drops that person's
        // hours from the pull).
        grouping: personMode ? 'person' : 'phase',
        categories: invoiceCats.map(cat => {
          const member = personMode
            ? (selProj?.teamMembers || []).find(m => m && m.togglName === cat.name)
            : null;
          return {
            name:      (member && String(member.invoiceName || '').trim()) || cat.name,
            hours:     cat.effHours,
            amount:    cat.effAmount,
            rate:      personMode ? (cat.rate || 0) : undefined,
            narrative: summaries[cat.name] || '',
          };
        }),
        totalHours:  effTotalHours,
        totalAmount: effTotalAmount,
        adjustments: allAdjustments.map(adj => ({
          name:        adj.entry.togglName,
          category:    adj.catName,
          togglHours:  adj.entry.hours,
          billedHours: adj.billedHrs,
          note:        adjustmentNotes[adj.catName] || '',
        })),
      };
      const res    = await fetch('/api/generate-hours-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'PDF generation failed');
      const bytes  = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
      const blob   = new Blob([bytes], { type: 'application/pdf' });
      const url    = URL.createObjectURL(blob);
      setPdfBlobBytes(bytes);
      setPdfModalUrl(url);
      setPdfModalFilename(result.filename || 'hours_invoice.pdf');
    } catch (err) {
      setPdfError(err.message || 'PDF generation failed');
    } finally {
      setPdfGenerating(false);
    }
  }

  async function saveDraft() {
    const selProj = projects.find(pr => String(pr.id) === String(apiProjId));
    if (!selProj || !apiResult) return;
    const client = clients.find(c => c.id === selProj.clientId);
    setDraftSaveStatus('saving');
    setDraftSaveError('');
    const row = buildHoursInvoiceRow({
      selProj, client, apiYear, apiMonth, apiResult,
      effTotalHours, effTotalAmount,
      adjustedHours, adjustmentNotes, summaries,
      status: 'Draft',
      pdfUrl: '',
    });
    const { error } = await supabase.from('hours_invoices')
      .upsert(row, { onConflict: 'invoice_number' });
    if (error) {
      setDraftSaveStatus('error');
      setDraftSaveError(error.message);
    } else {
      setDraftSaveStatus('saved');
      setLoadedStatus('Draft');
      loadHoursInvoices();
      setTimeout(() => setDraftSaveStatus(''), 2000);
    }
  }

  // Autosave (2026-06-29 per Cimon). Same 1.5s debounce as the
  // billing-summary autosave. Fires when narrative / adjustment /
  // pulled-result state changes, persisting through saveDraft so
  // edited summaries survive tab close, project switch, or browser
  // crash without explicit Save Draft clicks. Skips when:
  //   - No Toggl result yet (apiResult null)
  //   - No project picked
  //   - Loaded record is Final (autosaving would convert it back
  //     to Draft, which is the opposite of what the bookkeeper
  //     wants — Final stays Final until explicit Save to Records)
  // savingRef prevents reentrant saves if a save is mid-flight when
  // the next debounce settles.
  const savingRef = useRef(false);
  useEffect(() => {
    if (!apiResult) return;
    if (!apiProjId) return;
    if (loadedStatus === 'Final') return;
    const handle = setTimeout(async () => {
      if (savingRef.current) return;
      savingRef.current = true;
      try { await saveDraft(); }
      catch (e) { console.error('[hours-autosave]', e); }
      finally { savingRef.current = false; }
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaries, adjustedHours, adjustmentNotes, apiResult, apiProjId, apiMonth, apiYear, loadedStatus]);

  function loadDraft(record) {
    if (!record) return;
    const parts = record.invoice_number.split('-');
    const month = parseInt(parts[parts.length - 1]);
    const year  = parseInt(parts[parts.length - 2]);
    const proj  = projects.find(p => p.label === record.project_label);
    if (proj) setApiProjId(String(proj.id));
    if (!isNaN(month)) setApiMonth(month);
    if (!isNaN(year))  setApiYear(year);
    setApiResult({ categories: record.categories_snapshot || [] });
    setAdjustedHours(record.adjusted_hours || {});
    setAdjustmentNotes(record.adjustment_notes || {});
    setSummaries(record.summaries || {});
    setLoadedStatus(record.status || 'Draft');
    setDraftBanner(null);
    setApiPullError('');
  }

  async function saveToRecords() {
    if (!pdfBlobBytes) return;
    // Guard: refuse to write a partial row if the Toggl pull never ran or
    // got cleared. Production rows 26-Moskito-2026-03 and 26-Ptarmigan-2026-03
    // (created 2026-04-17) landed in this state — invoice + PDF persisted but
    // every other column NULL — because the prior implementation wrote a
    // 6-column finalize payload that depended on saveDraft having INSERTed
    // the row first. Now: refuse here, write the full 13-column row below.
    const selProj = projects.find(pr => String(pr.id) === String(apiProjId));
    const gate = canFinalizeHoursInvoice({ apiResult, effTotalHours, effTotalAmount, selProj });
    if (!gate.ok) {
      setSaveToRecordsError(gate.error);
      return;
    }
    const client = clients.find(c => c.id === selProj.clientId);
    const invoiceNumber = buildInvoiceNumber(selProj.label, apiYear, apiMonth);
    setSavingToRecords(true);
    setSaveToRecordsError('');
    try {
      const blob = new Blob([pdfBlobBytes], { type: 'application/pdf' });
      const storagePath = `${invoiceNumber}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('invoice-pdfs')
        .upload(storagePath, blob, { contentType: 'application/pdf', upsert: true });
      if (uploadError) throw new Error(uploadError.message);
      const { data: urlData } = await supabase.storage
        .from('invoice-pdfs')
        .createSignedUrl(storagePath, 60 * 60 * 24 * 365);
      const pdfUrl = urlData?.signedUrl || '';
      // Self-sufficient finalize: write every column the row needs to be
      // recoverable from itself. Column shape comes from the same helper
      // that saveDraft uses, so no drift between the two write paths.
      const row = buildHoursInvoiceRow({
        selProj, client, apiYear, apiMonth, apiResult,
        effTotalHours, effTotalAmount,
        adjustedHours, adjustmentNotes, summaries,
        status: 'Final',
        pdfUrl,
      });
      const { error: updateError } = await supabase.from('hours_invoices')
        .upsert(row, { onConflict: 'invoice_number' });
      if (updateError) throw new Error(updateError.message);
      setLoadedStatus('Final');
      loadHoursInvoices();
      URL.revokeObjectURL(pdfModalUrl);
      setPdfModalUrl(null);
      setPdfModalFilename('');
      setPdfBlobBytes(null);
    } catch (err) {
      setSaveToRecordsError(err.message);
    } finally {
      setSavingToRecords(false);
    }
  }

  // ── DERIVED ──
  // Effective hours/amount per category after applying adjustedHours overrides.
  // Logic was shared with the (removed) QB hours-invoice push
  // via computeEffectiveCategoryData in utils.js — same pattern as
  // calcBillingSummaryTotalDue. Pure helper, no React state inside.
  const effective = computeEffectiveCategoryData({
    categoriesSnapshot: apiResult?.categories || [],
    adjustedHours,
  });
  // Person-grouped billing (2026-08-23 — the Kubik switch). When the
  // project's CLIENT is set to hoursGrouping 'person', every downstream
  // consumer (editor boxes, PDF payload, save totals) reads the pivoted
  // person-shaped effective instead — one "category" per team member,
  // sorted by rate. Same shape, so nothing else changes. Category-mode
  // clients are untouched.
  const projForMode = projects.find(pr => String(pr.id) === String(apiProjId));
  const clientForMode = projForMode ? clients.find(c => c.id === projForMode.clientId) : null;
  const personMode = clientHoursGrouping(clientForMode) === 'person';
  const billingEffective = personMode
    ? groupHoursByPersonAsCategories(effective.categories, adjustedHours)
    : effective;
  const effCategoryData = billingEffective.categories;
  const effTotalHours   = billingEffective.totalHours;
  const effTotalAmount  = billingEffective.totalAmount;

  // ── RENDER ──
  return (
    <div style={{ padding: '20px 18px' }}>
      {/* PDF preview modal */}
      {pdfModalUrl && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#1a1a1a' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{pdfModalFilename}</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <a href={pdfModalUrl} download={pdfModalFilename}
                style={{ padding: '6px 16px', background: '#fff', color: '#1a1a1a', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>
                Download
              </a>
              <button onClick={saveToRecords} disabled={savingToRecords}
                style={{ padding: '6px 16px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: savingToRecords ? 'wait' : 'pointer', opacity: savingToRecords ? 0.7 : 1 }}>
                {savingToRecords ? 'Saving…' : 'Save to Records'}
              </button>
              {saveToRecordsError && <span style={{ fontSize: 11, color: '#ff6b6b' }}>{saveToRecordsError}</span>}
              <button onClick={() => { URL.revokeObjectURL(pdfModalUrl); setPdfModalUrl(null); setPdfModalFilename(''); setPdfBlobBytes(null); }}
                style={{ padding: '6px 16px', background: 'none', border: '1px solid #555', color: '#fff', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
          <iframe src={pdfModalUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="Hours Invoice PDF Preview" />
        </div>
      )}

      {/* ── Recent Hours Invoices ── */}
      {(hoursInvoicesLoading || hoursInvoices.length > 0) && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Recent Designer Hours Invoices</div>
          {hoursInvoicesLoading ? (
            <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  {['Invoice', 'Period', 'Hours', 'Amount', 'Status', 'Payments', 'PDF'].map(h => (
                    <th key={h} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600, color: '#888', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hoursInvoices.map(inv => (
                  <tr key={inv.invoice_number}
                    onClick={() => {
                      // Row click dispatcher: Draft loads into the live editor
                      // for further editing (existing behavior). Finalized rows
                      // (Final/Partial/Paid) open the read-only inspection
                      // modal — the row's draft-edit data becomes invisible
                      // otherwise.
                      if (inv.status === 'Draft') loadDraft(inv);
                      else setInspectingInv(inv);
                    }}
                    title={inv.status === 'Draft' ? 'Click to load this draft' : 'Click to inspect this invoice (read-only) — hours, adjustments, narratives'}
                    style={{ borderBottom: '1px solid #f8f8f8', cursor: 'pointer' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{inv.invoice_number}</td>
                    <td style={{ padding: '6px 8px', color: '#555' }}>{inv.invoice_date}</td>
                    <td style={{ padding: '6px 8px' }}>{inv.total_hours != null ? Number(inv.total_hours).toFixed(1) : '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtFull(inv.total_amount)}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: inv.status === 'Paid' ? '#e8f5ee' : (inv.status === 'Partial' ? '#fde9f2' : (inv.status === 'Final' ? '#f0f7f0' : '#f5f5f5')),
                        color: inv.status === 'Paid' ? '#1A5C38' : (inv.status === 'Partial' ? '#A8005A' : (inv.status === 'Final' ? '#1A5C38' : '#666')),
                      }}>{inv.status}</span>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {(function() {
                        var paid = calcPaidForDocument(paymentAllocations, 'hours_invoice', inv.invoice_number);
                        var amt = parseFloat(inv.total_amount) || 0;
                        var label = paid > 0
                          ? (paid >= amt - 0.005 ? 'Paid' : 'Partial · ' + fmtFull(paid))
                          : '—';
                        return inv.status === 'Final' || inv.status === 'Partial' || inv.status === 'Paid' ? (
                          <button
                            onClick={e => { e.stopPropagation(); setPaymentsModalInv(inv); }}
                            style={{ padding: '2px 8px', fontSize: 11, color: '#1a1a1a', background: '#fff', border: '0.5px solid #D8D7D2', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {label} ›
                          </button>
                        ) : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>;
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {inv.pdf_url
                        ? <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#1a1a1a', fontSize: 11, fontWeight: 600 }}>↓ PDF</a>
                        : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Direct Toggl Pull ── */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Pull from Toggl API</div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>PROJECT</label>
          <select value={apiProjId} onChange={e => { setApiProjId(e.target.value); setApiResult(null); setApiPullError(''); setSummaries({}); setLoadedStatus(null); }}
            style={{ ...inp, maxWidth: 360, width: '100%' }}>
            <option value="">— Select project —</option>
            {activeProjects(projects, (projects.find(pr => String(pr.id) === String(apiProjId)) || {}).label).filter(proj => proj.togglPrefix).map(proj => (
              <option key={proj.id} value={proj.id}>{proj.label} ({proj.togglPrefix})</option>
            ))}
          </select>
          {projects.filter(proj => proj.togglPrefix).length === 0 && (
            <div style={{ fontSize: 11, color: '#e65100', marginTop: 4 }}>No projects have a Toggl prefix set. Add one in Admin → Projects.</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>MONTH</label>
            <select value={apiMonth} onChange={e => { setApiMonth(Number(e.target.value)); setApiResult(null); setSummaries({}); setLoadedStatus(null); }} style={{ ...inp }}>
              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>YEAR</label>
            <select value={apiYear} onChange={e => { setApiYear(Number(e.target.value)); setApiResult(null); setSummaries({}); setLoadedStatus(null); }} style={{ ...inp }}>
              {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button onClick={pullFromToggl} disabled={apiPulling || !apiProjId}
            style={{ padding: '8px 18px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: apiPulling || !apiProjId ? 'not-allowed' : 'pointer', opacity: !apiProjId ? 0.5 : 1 }}>
            {apiPulling ? 'Pulling…' : 'Pull Hours'}
          </button>
        </div>

        {apiPullError && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 12 }}>{apiPullError}</div>}

        {draftBanner && (
          <div style={{ background: '#fff', border: '0.5px solid #E0E0DC', borderLeft: '3px solid #F5C800', borderRadius: 4, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, fontSize: 13, color: '#111' }}>
              {draftBanner.record?.status === 'Final'
                ? <>A finalized invoice exists for <strong>{draftBanner.projLabel}</strong> — <strong>{draftBanner.monthName} {apiYear}</strong>. Load it to edit hours and summaries, or Pull Fresh to re-pull from Toggl <span style={{ color: '#888' }}>(clears saved summaries)</span>.</>
                : <>A draft exists for <strong>{draftBanner.projLabel}</strong> — <strong>{draftBanner.monthName} {apiYear}</strong>.</>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => loadDraft(draftBanner.record)}
                style={{ padding: '6px 14px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {draftBanner.record?.status === 'Final' ? 'Load Record' : 'Load Draft'}
              </button>
              <button
                onClick={() => {
                  // Confirm before clobbering saved summaries (2026-06-29
                  // per Cimon). Pull Fresh re-pulls from Toggl AND wipes
                  // the in-memory summaries; saving after Pull Fresh
                  // overwrites the DB row with the new blank state.
                  // Easy to lose narrative work without a confirm gate.
                  var msg = draftBanner.record?.status === 'Final'
                    ? 'Re-pull Toggl for this period? This will discard the saved summaries on the existing Final invoice. (Use Load Record instead to keep your edited narratives.)'
                    : 'Re-pull Toggl for this period? Any saved summaries on this draft will be cleared. (Use Load Draft instead to keep your edited narratives.)';
                  if (window.confirm(msg)) _doPullFresh(draftBanner.requestBody);
                }}
                style={{ padding: '6px 14px', background: 'none', border: '1px solid #aaa', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#555' }}>
                Pull Fresh
              </button>
              <button onClick={() => setDraftBanner(null)}
                style={{ padding: '4px 8px', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa', lineHeight: 1 }}>
                ×
              </button>
            </div>
          </div>
        )}

        {apiResult && (function() {
          // Derive the editor's display-side props at the parent. The
          // inline editor block (pre-2026-05-11) computed these inside
          // the JSX; lifting them here keeps HoursInvoiceEditor purely
          // presentational and lets the same component serve both the
          // live draft-edit flow and the read-only inspection modal.
          const selProj   = projects.find(pr => String(pr.id) === String(apiProjId));
          const editClient = selProj ? clients.find(c => c.id === selProj.clientId) : null;
          const monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][apiMonth - 1];
          function copyText(txt, doneMsg) {
            (navigator.clipboard && navigator.clipboard.writeText
              ? navigator.clipboard.writeText(txt)
              : Promise.reject(new Error('clipboard unavailable'))
            ).then(function() { if (doneMsg) window.alert(doneMsg); })
             .catch(function() { window.prompt('Copy the text below:', txt); });
          }
          const unmatched = unmatchedTogglUsers(apiResult);
          return (
            <div>
            {unmatched.length > 0 && (
              <div data-testid="toggl-unmatched-users" style={{ border: HAIRLINE_AMBER, borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: ALERT_AMBER_TEXT, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Not on this invoice — {unmatched.length === 1 ? '1 Toggl user' : unmatched.length + ' Toggl users'} logged time on {selProj ? selProj.label : 'this project'} but match no one on its team table
                </div>
                {unmatched.map(function(u) {
                  return <div key={u.togglName} data-testid="toggl-unmatched-user">{unmatchedTogglUserLine(u)}</div>;
                })}
                <div style={{ marginTop: 6, fontSize: 11 }}>
                  Add the name under Projects → {selProj ? selProj.label : 'the project'} → Integrations → Toggl Name, spelled exactly as above, then Pull Fresh.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button onClick={function() {
                var header = (selProj ? selProj.label : '') + ' — ' + monthName + ' ' + apiYear + ' raw Toggl entries';
                copyText(buildAllRawEntriesText(billingEffective, header), 'All raw entries copied — paste into your Claude chat.');
              }}
                style={{ padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, cursor: 'pointer', color: '#555' }}>
                ⧉ Copy ALL raw entries
              </button>
            </div>
            <HoursInvoiceEditor
              onCopyRawEntries={function(cat) { copyText(buildRawEntriesText(cat), null); }}
              effective={billingEffective}
              adjustedHours={adjustedHours}
              adjustmentNotes={adjustmentNotes}
              summaries={summaries}
              project={selProj}
              client={editClient}
              monthName={monthName}
              year={apiYear}
              onAdjustedHoursChange={(key, val) => setAdjustedHours(prev => ({ ...prev, [key]: val }))}
              onAdjustmentNoteChange={(catName, val) => setAdjustmentNotes(prev => ({ ...prev, [catName]: val }))}
              onSummaryChange={(catName, val) => setSummaries(prev => ({ ...prev, [catName]: val }))}
              onSaveDraft={saveDraft}
              onSummarizeCategory={summarizeCategory}
              onConsolidateSummary={consolidateSummary}
              onGeneratePdf={generatePdf}
              draftSaveStatus={draftSaveStatus}
              draftSaveError={draftSaveError}
              summarizingCat={summarizingCat}
              consolidatingCat={consolidatingCat}
              pdfGenerating={pdfGenerating}
              pdfError={pdfError}
            />
            </div>
          );
        })()}
      </div>

      {/* ── CSV Upload (fallback) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
        <span style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>— or upload Toggl CSV manually —</span>
        <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <input ref={fileRef} type="file" accept=".csv" onChange={handleTogglFile} style={{ display: 'none' }} />
        <button onClick={() => fileRef.current.click()} style={{ padding: '8px 18px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Upload Toggl CSV
        </button>
        {togglData && <span style={{ fontSize: 12, color: '#888' }}>{togglData.rowCount} entries loaded</span>}
      </div>
      {togglError && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 16 }}>{togglError}</div>}
      {togglData && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Hours', value: togglData.totalHours.toFixed(1) + ' hrs' },
              { label: 'Total Billable', value: '$' + togglData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }) },
              { label: 'Projects Tracked', value: Object.keys(togglData.byProject).length },
            ].map(card => (
              <div key={card.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{card.value}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{card.label}</div>
              </div>
            ))}
          </div>
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['Project', 'Entries', 'Hours', 'Billable Amount'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(togglData.byProject).sort((a, b) => b[1].amount - a[1].amount).map(([proj, data], idx) => (
                  <tr key={proj} style={{ borderBottom: '1px solid #f5f5f5', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{proj}</td>
                    <td style={{ padding: '8px 12px', color: '#666' }}>{data.entries}</td>
                    <td style={{ padding: '8px 12px', color: '#666' }}>{data.hours.toFixed(1)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#111' }}>
                      {data.amount > 0 ? '$' + data.amount.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Payments management modal ── */}
      {paymentsModalInv && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 480, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{paymentsModalInv.invoice_number}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{paymentsModalInv.client_name} · {paymentsModalInv.invoice_date}</div>
              </div>
              <button
                onClick={() => setPaymentsModalInv(null)}
                style={{ padding: '4px 10px', fontSize: 11, color: '#1a1a1a', background: '#fff', border: '0.5px solid #D8D7D2', borderRadius: 4, cursor: 'pointer' }}>
                Close
              </button>
            </div>
            {(() => {
              const proj = projects.find(p => p.label === paymentsModalInv.project_label);
              const docCtx = {
                documentType: 'hours_invoice',
                documentId:   paymentsModalInv.invoice_number,
                clientId:     proj ? proj.clientId || '' : '',
                projectLabel: paymentsModalInv.project_label || '',
              };
              const entries = paymentEntriesForDocument(
                paymentAllocations, clientPayments,
                'hours_invoice', paymentsModalInv.invoice_number);
              return (
                <PaymentsPanel
                  amountDue={parseFloat(paymentsModalInv.total_amount) || 0}
                  entries={entries}
                  canManage={canManage}
                  userEmail={userEmail}
                  documentContext={docCtx}
                  onRecordPayment={(payment, allocations) => onPanelRecordPayment(paymentsModalInv, payment, allocations)}
                  onUpdatePayment={async (paymentId, allocationId, patch) => {
                    if (!ctx.updateClientPayment) return;
                    const res = await ctx.updateClientPayment(paymentId, allocationId, patch);
                    if (res && !res.ok) window.alert('Could not update payment: ' + (res.error && res.error.message || 'unknown'));
                  }}
                  onRemoveAllocation={allocId => onPanelRemoveAllocation(paymentsModalInv, allocId)} />
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Read-only inspection modal ── */}
      {/* Opens when the user clicks a finalized row (Final/Partial/Paid)
          in the Recent Hours Invoices table. Renders the same
          HoursInvoiceEditor used for the live draft-edit flow, with
          readOnly=true. The row's persisted columns flow into the
          editor's props; handlers are no-ops. Close via Escape (effect
          above), backdrop click, or the explicit Close button. */}
      {inspectingInv && (function() {
        // Derive the editor's props from the row's persisted state. The
        // invoice_number natural key is `{ProjectLabel}-{YYYY}-{MM}` so
        // year/month parse trivially. Project + client come from the
        // current directory lookups; gracefully null if the records
        // were deleted post-finalize.
        const parts = String(inspectingInv.invoice_number || '').split('-');
        const month = parseInt(parts[parts.length - 1], 10);
        const yearNum = parseInt(parts[parts.length - 2], 10);
        const monthName = !isNaN(month) ? ['January','February','March','April','May','June','July','August','September','October','November','December'][month - 1] : '';
        const proj = projects.find(p => p.label === inspectingInv.project_label) || null;
        const cli  = proj ? clients.find(c => c.id === proj.clientId) : null;
        // Compute effective category data from the persisted snapshot +
        // adjustments. Same helper the live editor uses — and the same
        // person pivot (2026-09-04, Cimon: the modal must show employees,
        // not stage of design, for person-grouped clients). The snapshot
        // is always the RAW phase pull; grouping is per-client presentation.
        const inspectPhase = computeEffectiveCategoryData({
          categoriesSnapshot: inspectingInv.categories_snapshot || [],
          adjustedHours:      inspectingInv.adjusted_hours      || {},
        });
        const inspectEffective = clientHoursGrouping(cli) === 'person'
          ? groupHoursByPersonAsCategories(inspectPhase.categories, inspectingInv.adjusted_hours || {})
          : inspectPhase;
        const noop = () => {};
        return (
          <div
            data-testid="hours-inspection-modal"
            onClick={() => setInspectingInv(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 20px' }}>
            <div
              onClick={e => e.stopPropagation()}
              style={{ width: '100%', maxWidth: 980, background: '#fff', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', padding: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{inspectingInv.invoice_number}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    {inspectingInv.client_name} · {inspectingInv.invoice_date}
                    {inspectingInv.status && <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: inspectingInv.status === 'Paid' ? '#e8f5ee' : (inspectingInv.status === 'Partial' ? '#fde9f2' : '#f0f7f0'), color: inspectingInv.status === 'Paid' ? '#1A5C38' : (inspectingInv.status === 'Partial' ? '#A8005A' : '#1A5C38') }}>{inspectingInv.status}</span>}
                  </div>
                </div>
                <button
                  data-testid="hours-inspection-close"
                  onClick={() => setInspectingInv(null)}
                  style={{ padding: '4px 10px', fontSize: 11, color: '#1a1a1a', background: '#fff', border: '0.5px solid #D8D7D2', borderRadius: 4, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
              <HoursInvoiceEditor
                readOnly={true}
                effective={inspectEffective}
                adjustedHours={inspectingInv.adjusted_hours || {}}
                adjustmentNotes={inspectingInv.adjustment_notes || {}}
                summaries={inspectingInv.summaries || {}}
                project={proj}
                client={cli}
                monthName={monthName}
                year={yearNum}
                onAdjustedHoursChange={noop}
                onAdjustmentNoteChange={noop}
                onSummaryChange={noop}
                onSaveDraft={noop}
                onSummarizeCategory={noop}
                onConsolidateSummary={noop}
                onGeneratePdf={noop}
                draftSaveStatus=""
                draftSaveError=""
                summarizingCat={null}
                consolidatingCat={null}
                pdfGenerating={false}
                pdfError=""
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
